import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Lock, Plus, Trash2, Upload, Loader2, GripVertical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';

// dev-builder-200 Fase 3: editor de 11 secciones + upload de archivos + curado
// de unidades para la landing pública.
//
// COLORES: el pedido original quería un objeto {title_color, subtitle_color,
// bg_color} DUPLICADO dentro de cada una de las 8 secciones (24 campos sueltos)
// Y ADEMÁS colores globales en Tipografía -- dos sistemas contradictorios. El
// backend (routes/dev_builder.py, ya probado en LAB) centraliza esto: título/
// subtítulo de sección son GLOBALES (tipografia.section_title_color/
// section_subtitle_color, un solo lugar para cambiarlos todos a la vez) y el
// FONDO sí es por-sección (tipografia.section_background_colors[key]). Cada
// sección de abajo trae su selector de fondo; el título/subtítulo se edita una
// sola vez en "10. Tipografía".
//
// UNIDADES: publish_on_landing / landing_image NO viven en el doc de la
// landing -- son campos de dev_units, con su propio endpoint
// (PATCH /dev-builder/data/units/{id}). Por eso se guardan AL TOQUE (no
// esperan al botón "Guardar cambios", que solo hace PUT del doc de landing).

const SECTION_ORDER = [
  'hero', 'proyectos', 'disponibilidad', 'caracteristicas', 'galeria',
  'faq', 'contacto', 'redes', 'footer', 'tipografia', 'permisos',
];

async function uploadDevBuilderFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/dev-builder/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data; // {url, filename, type}
}

function ColorField({ label, value, defaultValue, onChange }) {
  const current = value || defaultValue;
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <Label className="text-xs text-gray-300">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-8 h-8 rounded border border-slate-600 shrink-0"
            style={{ backgroundColor: current }}
            title={current}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3">
          <input
            type="color"
            value={current}
            onChange={(e) => onChange(e.target.value)}
            className="w-24 h-24 cursor-pointer border-0 p-0 bg-transparent"
          />
          <p className="text-xs text-center text-gray-500 mt-1 font-mono">{current}</p>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function UploadButton({ label, accept, uploading, multiple = false, onFiles }) {
  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 border border-slate-600 rounded cursor-pointer text-sm text-gray-100 hover:bg-slate-700 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {uploading ? 'Subiendo...' : label}
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </label>
  );
}

export default function SidebarEditor({
  landing,
  developments = [],
  units = [],
  expandedSections,
  toggleSection,
  updateSection,
  onDelete,
  userRole,
}) {
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const [uploadingField, setUploadingField] = useState(null);
  const [unitSaving, setUnitSaving] = useState({});
  const [unitOverrides, setUnitOverrides] = useState({});
  const [dragIndex, setDragIndex] = useState(null);
  const [dropActive, setDropActive] = useState(false);

  const development = developments.find((d) => d.id === landing.development_id);
  const tipografia = landing.tipografia || {};
  const bgColors = tipografia.section_background_colors || {};

  const setSectionBg = (key, color) => {
    updateSection('tipografia', { section_background_colors: { ...bgColors, [key]: color } });
  };

  const uploadInto = async (fieldKey, file, onDone) => {
    setUploadingField(fieldKey);
    try {
      const data = await uploadDevBuilderFile(file);
      onDone(data.url);
      toast.success('Archivo subido');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error subiendo el archivo');
    } finally {
      setUploadingField(null);
    }
  };

  const getUnit = (unit) => ({ ...unit, ...(unitOverrides[unit.id] || {}) });

  const patchUnit = async (unitId, patch) => {
    setUnitSaving((s) => ({ ...s, [unitId]: true }));
    try {
      await api.patch(`/dev-builder/data/units/${unitId}`, patch);
      setUnitOverrides((prev) => ({ ...prev, [unitId]: { ...prev[unitId], ...patch } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar la unidad');
    } finally {
      setUnitSaving((s) => ({ ...s, [unitId]: false }));
    }
  };

  // Helper genérico para render de header + colapsable, con selector de fondo
  // por-sección (excepto Hero, que tiene su propio hero_bg_color en Tipografía,
  // y Permisos/Tipografía, que no pintan una sección visible en la pública).
  const renderSectionHeader = (sectionName, displayName) => (
    <button onClick={() => toggleSection(sectionName)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700">
      <span className="font-semibold text-gray-100">{displayName}</span>
      {expandedSections[sectionName] ? <ChevronUp className="h-5 w-5 text-gray-300" /> : <ChevronDown className="h-5 w-5 text-gray-300" />}
    </button>
  );

  const renderSection = (sectionName, displayName, content, { adminOnly = false, hasBg = true } = {}) => {
    if (adminOnly && !isAdmin) return null;
    return (
      <div key={sectionName} className="border-b border-slate-700">
        <div className="bg-slate-900/40">{renderSectionHeader(sectionName, displayName)}</div>
        {expandedSections[sectionName] && (
          <div className="p-4 space-y-4">
            {adminOnly && (
              <div className="flex items-center gap-2 text-sm text-yellow-300 bg-yellow-900/30 p-2 rounded">
                <Lock className="h-4 w-4" /><span>Sección solo para administradores</span>
              </div>
            )}
            {content}
            {hasBg && (
              <div className="pt-2 border-t">
                <ColorField
                  label="Color de fondo de esta sección"
                  value={bgColors[sectionName]}
                  defaultValue="#ffffff"
                  onChange={(v) => setSectionBg(sectionName, v)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── 1. HERO ────────────────────────────────────────────────────────────
  const hero = landing.hero || {};
  const heroSection = renderSection('hero', '1. Hero', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Título</label>
        <Input value={hero.title || ''} onChange={(e) => updateSection('hero', { title: e.target.value })} />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">Subtítulo</label>
        <Input value={hero.subtitle || ''} onChange={(e) => updateSection('hero', { subtitle: e.target.value })} />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">Texto del botón CTA</label>
        <Input value={hero.cta_text || ''} onChange={(e) => updateSection('hero', { cta_text: e.target.value })} />
      </div>
      <div>
        <label className="text-sm font-medium block mb-1 text-gray-100">Imagen o video de fondo</label>
        <UploadButton
          label="Subir imagen o video"
          accept="image/*,video/mp4,video/webm"
          uploading={uploadingField === 'hero'}
          onFiles={(files) => {
            const file = files[0];
            const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
            uploadInto('hero', file, (url) => updateSection('hero', { media_url: url, media_type: mediaType }));
          }}
        />
        {hero.media_url && (
          <div className="mt-2 rounded overflow-hidden border">
            {hero.media_type === 'video' ? (
              <video src={hero.media_url} controls className="w-full h-32 object-cover" />
            ) : (
              <img src={hero.media_url} alt="" className="w-full h-32 object-cover" />
            )}
          </div>
        )}
      </div>
      <div className="pt-2 border-t space-y-3">
        <p className="text-xs font-semibold text-gray-400">Pestaña del navegador (solo se ve en la landing publicada, no aquí en el editor)</p>
        <div>
          <label className="text-sm font-medium block mb-1 text-gray-100">Nombre de la pestaña</label>
          <Input value={hero.page_title || ''} onChange={(e) => updateSection('hero', { page_title: e.target.value })} placeholder={landing.name} />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1 text-gray-100">Ícono de la pestaña (favicon)</label>
          <UploadButton
            label="Subir ícono"
            accept="image/*"
            uploading={uploadingField === 'favicon'}
            onFiles={(files) => uploadInto('favicon', files[0], (url) => updateSection('hero', { favicon_url: url }))}
          />
        </div>
        {/* Mockup de la pestaña -- NO un <title>/<link> real: esto es DevLandingPublic.js
            embebido sin iframe dentro del editor (React 19 hoistea <title>/<link> al
            <head> real desde CUALQUIER punto del árbol) -- ponerlos ahí le robaría el
            título/ícono a la pestaña del propio editor mientras alguien edita. */}
        <div className="flex items-center gap-2 bg-slate-900 border border-slate-600 rounded-t px-3 py-2 max-w-[220px]">
          {hero.favicon_url ? (
            <img src={hero.favicon_url} alt="" className="h-4 w-4 object-contain flex-shrink-0" />
          ) : (
            <div className="h-4 w-4 rounded-sm bg-slate-600 flex-shrink-0" />
          )}
          <span className="text-xs text-gray-200 truncate">{hero.page_title || landing.name || 'Sin título'}</span>
        </div>
      </div>
    </div>
  ), { hasBg: false });

  // ── 2. PROYECTOS ───────────────────────────────────────────────────────
  const proyectos = landing.proyectos || {};
  const proyectosSection = renderSection('proyectos', '2. Proyectos', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Título de la sección</label>
        <Input value={proyectos.title || ''} onChange={(e) => updateSection('proyectos', { title: e.target.value })} />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">Descripción</label>
        <Textarea value={proyectos.description || ''} onChange={(e) => updateSection('proyectos', { description: e.target.value })} rows={3} />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={proyectos.show_logo ?? true} onCheckedChange={(v) => updateSection('proyectos', { show_logo: !!v })} />
        Mostrar logo (propio de la landing, o el del desarrollo si no hay uno)
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={proyectos.show_development_images ?? true} onCheckedChange={(v) => updateSection('proyectos', { show_development_images: !!v })} />
        Mostrar imágenes del desarrollo
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={proyectos.show_map ?? true} onCheckedChange={(v) => updateSection('proyectos', { show_map: !!v })} />
        Mostrar mapa
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={proyectos.show_price_range ?? true} onCheckedChange={(v) => updateSection('proyectos', { show_price_range: !!v })} />
        Mostrar precio desde
      </label>
      <div>
        <label className="text-sm font-medium block mb-1 text-gray-100">Logo de la landing</label>
        <UploadButton
          label="Subir logo"
          accept="image/*"
          uploading={uploadingField === 'proyectos-logo'}
          onFiles={(files) => uploadInto('proyectos-logo', files[0], (url) => updateSection('proyectos', { logo_url: url }))}
        />
        {proyectos.logo_url ? (
          <img src={proyectos.logo_url} alt="" className="mt-2 h-16 object-contain" />
        ) : development?.logo_url ? (
          <div className="mt-2">
            <img src={development.logo_url} alt="" className="h-16 object-contain" />
            <p className="text-xs text-gray-400 mt-1">Sin logo propio — se usa el logo del desarrollo.</p>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Sin logo todavía.</p>
        )}
      </div>
    </div>
  ));

  // ── 3. DISPONIBILIDAD ──────────────────────────────────────────────────
  const disponibilidad = landing.disponibilidad || {};
  const publishedCount = units.filter((u) => getUnit(u).publish_on_landing).length;
  const disponibilidadSection = renderSection('disponibilidad', '3. Disponibilidad', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Título de la sección</label>
        <Input value={disponibilidad.title || ''} onChange={(e) => updateSection('disponibilidad', { title: e.target.value })} />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">Descripción</label>
        <Textarea value={disponibilidad.description || ''} onChange={(e) => updateSection('disponibilidad', { description: e.target.value })} rows={2} />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={disponibilidad.show_status_filter ?? true} onCheckedChange={(v) => updateSection('disponibilidad', { show_status_filter: !!v })} />
        Mostrar filtro por estado
      </label>
      <div className="text-xs text-gray-300 p-2 bg-slate-700 rounded">
        {publishedCount} de {units.length} unidades marcadas para la landing (se guarda al instante, no espera "Guardar cambios")
      </div>
      <div className="max-h-96 overflow-y-auto border rounded divide-y">
        {units.map((rawUnit) => {
          const unit = getUnit(rawUnit);
          const saving = !!unitSaving[unit.id];
          return (
            <div key={unit.id} className="p-2 flex items-center gap-2">
              <Checkbox
                checked={!!unit.publish_on_landing}
                disabled={saving}
                onCheckedChange={(v) => patchUnit(unit.id, { publish_on_landing: !!v })}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-gray-100">{unit.label || unit.type}</p>
                <p className="text-xs text-gray-400">{unit.type} · {unit.status}</p>
              </div>
              {saving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              {unit.publish_on_landing && (
                <div className="flex items-center gap-1">
                  {unit.landing_image && <img src={unit.landing_image} alt="" className="h-8 w-8 object-cover rounded" />}
                  <label className="text-xs text-blue-400 cursor-pointer hover:underline">
                    {unit.landing_image ? 'Cambiar' : 'Imagen'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        setUploadingField(`unit-${unit.id}`);
                        uploadDevBuilderFile(file)
                          .then((data) => patchUnit(unit.id, { landing_image: data.url }))
                          .catch(() => toast.error('Error subiendo la imagen'))
                          .finally(() => setUploadingField(null));
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  ));

  // ── 4. CARACTERÍSTICAS ─────────────────────────────────────────────────
  const caracteristicas = landing.caracteristicas || {};
  const caracteristicasSection = renderSection('caracteristicas', '4. Características', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Título de la sección</label>
        <Input value={caracteristicas.title || ''} onChange={(e) => updateSection('caracteristicas', { title: e.target.value })} />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">Descripción</label>
        <Textarea value={caracteristicas.description || ''} onChange={(e) => updateSection('caracteristicas', { description: e.target.value })} rows={2} />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={caracteristicas.show_development_amenities ?? true} onCheckedChange={(v) => updateSection('caracteristicas', { show_development_amenities: !!v })} />
        Mostrar amenidades
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={caracteristicas.show_icons ?? true} onCheckedChange={(v) => updateSection('caracteristicas', { show_icons: !!v })} />
        Mostrar íconos junto a cada amenidad
      </label>
      <div className="text-xs text-gray-300 p-2 bg-slate-700 rounded">
        Amenidades (no editables aquí — salen de las unidades marcadas "para la landing" en la sección 3. Disponibilidad):
        {(() => {
          const unified = [...new Set(units.filter((u) => getUnit(u).publish_on_landing).flatMap((u) => u.amenities || []))];
          return unified.length ? ' ' + unified.join(', ') : ' ninguna en las unidades publicadas todavía';
        })()}
      </div>
    </div>
  ));

  // ── 5. GALERÍA ──────────────────────────────────────────────────────────
  const galeria = landing.galeria || {};
  const images = galeria.images || [];

  const addGalleryFiles = async (files) => {
    if (images.length + files.length > 20) {
      toast.error('Máximo 20 imágenes en la galería');
      return;
    }
    setUploadingField('galeria');
    try {
      const uploaded = [];
      for (const file of files) {
        const data = await uploadDevBuilderFile(file);
        uploaded.push({ url: data.url, caption: '' });
      }
      updateSection('galeria', { images: [...images, ...uploaded] });
      toast.success(`${uploaded.length} imagen(es) agregada(s)`);
    } catch (err) {
      toast.error('Error subiendo imágenes');
    } finally {
      setUploadingField(null);
    }
  };
  const reorderImages = (from, to) => {
    if (from === to || from == null) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    updateSection('galeria', { images: next });
  };
  const updateImageCaption = (idx, caption) => {
    updateSection('galeria', { images: images.map((img, i) => (i === idx ? { ...img, caption } : img)) });
  };
  const removeImage = (idx) => {
    updateSection('galeria', { images: images.filter((_, i) => i !== idx) });
  };

  const galeriaSection = renderSection('galeria', '5. Galería', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Título de la sección</label>
        <Input value={galeria.title || ''} onChange={(e) => updateSection('galeria', { title: e.target.value })} />
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropActive(false);
          addGalleryFiles(Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/')));
        }}
        className={`border-2 border-dashed rounded p-4 text-center text-sm ${dropActive ? 'border-blue-500 bg-blue-900/20' : 'border-slate-600'}`}
      >
        <p className="text-gray-300 mb-2">Arrastra imágenes aquí, o</p>
        <UploadButton label="elegir imágenes" accept="image/*" multiple uploading={uploadingField === 'galeria'} onFiles={addGalleryFiles} />
        <p className="text-xs text-gray-400 mt-2">{images.length}/20 imágenes</p>
      </div>
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {images.map((img, idx) => (
            <div
              key={img.url + idx}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { reorderImages(dragIndex, idx); setDragIndex(null); }}
              className="border rounded overflow-hidden bg-gray-50 cursor-move"
            >
              <div className="relative">
                <img src={img.url} alt="" className="w-full h-20 object-cover" />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white"
                >
                  <Trash2 className="h-3 w-3 text-red-600" />
                </button>
                <GripVertical className="absolute top-1 left-1 h-4 w-4 text-white drop-shadow" />
              </div>
              <Input
                value={img.caption || ''}
                onChange={(e) => updateImageCaption(idx, e.target.value)}
                placeholder="Descripción"
                className="border-0 text-xs h-7 rounded-none"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  ));

  // ── 6. FAQ ──────────────────────────────────────────────────────────────
  const faq = landing.faq || {};
  const faqItems = faq.items || [];
  const updateFaqItem = (idx, field, value) => {
    updateSection('faq', { items: faqItems.map((it, i) => (i === idx ? { ...it, [field]: value } : it)) });
  };
  const addFaqItem = () => updateSection('faq', { items: [...faqItems, { question: '', answer: '' }] });
  const removeFaqItem = (idx) => updateSection('faq', { items: faqItems.filter((_, i) => i !== idx) });

  const faqSection = renderSection('faq', '6. Preguntas Frecuentes', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Título de la sección</label>
        <Input value={faq.title || ''} onChange={(e) => updateSection('faq', { title: e.target.value })} />
      </div>
      <div className="space-y-3">
        {faqItems.map((item, idx) => (
          <div key={idx} className="border border-slate-600 rounded p-3 space-y-2 bg-slate-700/50">
            <Input value={item.question} onChange={(e) => updateFaqItem(idx, 'question', e.target.value)} placeholder="Pregunta" />
            <Textarea value={item.answer} onChange={(e) => updateFaqItem(idx, 'answer', e.target.value)} placeholder="Respuesta" rows={2} />
            <Button variant="ghost" size="sm" onClick={() => removeFaqItem(idx)}><Trash2 className="h-4 w-4 mr-1" /> Quitar</Button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addFaqItem}><Plus className="h-4 w-4 mr-1" /> Agregar pregunta</Button>
    </div>
  ));

  // ── 7. CONTACTO ─────────────────────────────────────────────────────────
  const contacto = landing.contacto || {};
  const contactoSection = renderSection('contacto', '7. Contacto', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-100">Teléfono</label>
        <Input value={contacto.phone || ''} onChange={(e) => updateSection('contacto', { phone: e.target.value })} placeholder="+52 555 123 4567" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">Email</label>
        <Input type="email" value={contacto.email || ''} onChange={(e) => updateSection('contacto', { email: e.target.value })} placeholder="contacto@empresa.com" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">WhatsApp</label>
        <Input value={contacto.whatsapp || ''} onChange={(e) => updateSection('contacto', { whatsapp: e.target.value })} placeholder="+52 555 123 4567" />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-100">
        <Checkbox checked={contacto.show_contact_form ?? true} onCheckedChange={(v) => updateSection('contacto', { show_contact_form: !!v })} />
        Mostrar formulario de contacto
      </label>
    </div>
  ));

  // ── 8. REDES SOCIALES ──────────────────────────────────────────────────
  const redes = landing.redes || {};
  const redesSection = renderSection('redes', '8. Redes Sociales', (
    <div className="space-y-4">
      {['facebook', 'instagram', 'youtube', 'linkedin'].map((key) => (
        <div key={key}>
          <label className="text-sm font-medium capitalize text-gray-100">{key}</label>
          <Input value={redes[key] || ''} onChange={(e) => updateSection('redes', { [key]: e.target.value })} placeholder={`https://${key}.com/...`} />
        </div>
      ))}
      <p className="text-xs text-gray-400">Se muestra el ícono solo de las redes con URL cargada.</p>
    </div>
  ));

  // ── 9. FOOTER ───────────────────────────────────────────────────────────
  const footer = landing.footer || {};
  const footerSection = renderSection('footer', '9. Footer', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium block mb-1 text-gray-100">Logo del footer</label>
        <UploadButton
          label="Subir logo"
          accept="image/*"
          uploading={uploadingField === 'footer-logo'}
          onFiles={(files) => uploadInto('footer-logo', files[0], (url) => updateSection('footer', { logo_url: url }))}
        />
        {footer.logo_url && <img src={footer.logo_url} alt="" className="mt-2 h-12 object-contain" />}
      </div>
      <div>
        <label className="text-sm font-medium text-gray-100">URL de aviso de privacidad</label>
        <Input value={footer.privacy_url || ''} onChange={(e) => updateSection('footer', { privacy_url: e.target.value })} placeholder="https://..." />
      </div>
    </div>
  ));

  // ── 10. TIPOGRAFÍA (colores GLOBALES + fuente) ─────────────────────────
  const tipografiaSection = renderSection('tipografia', '10. Tipografía', (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium block mb-1 text-gray-100">Fuente personalizada (.ttf / .otf)</label>
        <UploadButton
          label="Subir fuente"
          accept=".ttf,.otf"
          uploading={uploadingField === 'font'}
          onFiles={(files) => {
            const file = files[0];
            const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
            if (!['.ttf', '.otf'].includes(ext)) {
              toast.error('Solo se aceptan archivos .ttf o .otf');
              return;
            }
            uploadInto('font', file, (url) => updateSection('tipografia', { font_file_url: url }));
          }}
        />
        {tipografia.font_file_url && (
          <p className="text-xs text-gray-400 mt-1 truncate">Fuente cargada: {tipografia.font_file_url}</p>
        )}
      </div>
      <div className="pt-2 border-t space-y-1">
        <p className="text-xs font-semibold text-gray-400 mb-1">Hero</p>
        <ColorField label="Título del hero" value={tipografia.hero_title_color} defaultValue="#ffffff" onChange={(v) => updateSection('tipografia', { hero_title_color: v })} />
        <ColorField label="Subtítulo del hero" value={tipografia.hero_subtitle_color} defaultValue="#ffffff" onChange={(v) => updateSection('tipografia', { hero_subtitle_color: v })} />
        <ColorField label="Fondo del hero (sin imagen/video)" value={tipografia.hero_bg_color} defaultValue="#667eea" onChange={(v) => updateSection('tipografia', { hero_bg_color: v })} />
      </div>
      <div className="pt-2 border-t space-y-1">
        <p className="text-xs font-semibold text-gray-400 mb-1">General (todas las secciones)</p>
        <ColorField label="Título de sección" value={tipografia.section_title_color} defaultValue="#111827" onChange={(v) => updateSection('tipografia', { section_title_color: v })} />
        <ColorField label="Subtítulo de sección" value={tipografia.section_subtitle_color} defaultValue="#4b5563" onChange={(v) => updateSection('tipografia', { section_subtitle_color: v })} />
        <ColorField label="Texto de cuerpo" value={tipografia.text_color} defaultValue="#333333" onChange={(v) => updateSection('tipografia', { text_color: v })} />
        <ColorField label="Enlaces" value={tipografia.link_color} defaultValue="#0066cc" onChange={(v) => updateSection('tipografia', { link_color: v })} />
        <ColorField label="Botones" value={tipografia.accent_color} defaultValue="#0066cc" onChange={(v) => updateSection('tipografia', { accent_color: v })} />
      </div>
    </div>
  ), { hasBg: false });

  // ── 11. PERMISOS (admin-only) ──────────────────────────────────────────
  const permisosSection = renderSection('permisos', '11. Permisos', (
    <div className="space-y-4 text-sm">
      <div className="space-y-1 text-gray-300">
        <p>Publicado: <strong>{landing.is_published ? 'Sí' : 'No'}</strong></p>
        <p>Creada: {landing.created_at ? new Date(landing.created_at).toLocaleString('es-MX') : '-'}</p>
        <p>Última edición: {landing.updated_at ? new Date(landing.updated_at).toLocaleString('es-MX') : '-'}</p>
      </div>
      {/* Publicar/Despublicar vive arriba, junto a "Guardar cambios" (DevBuilderPage.js)
          -- estaba escondido aquí, dentro de una sección colapsada por defecto. */}

      <div className="pt-3 border-t">
        <Button className="w-full" variant="destructive" onClick={() => onDelete?.()}>
          Borrar landing
        </Button>
      </div>
    </div>
  ), { adminOnly: true, hasBg: false });

  const sectionsByKey = {
    hero: heroSection, proyectos: proyectosSection, disponibilidad: disponibilidadSection,
    caracteristicas: caracteristicasSection, galeria: galeriaSection, faq: faqSection,
    contacto: contactoSection, redes: redesSection, footer: footerSection,
    tipografia: tipografiaSection, permisos: permisosSection,
  };

  return (
    <div className="divide-y divide-slate-700">
      {SECTION_ORDER.map((key) => sectionsByKey[key])}
    </div>
  );
}
